import React, { useEffect, useMemo, useState, useRef, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { StarIcon, SparklesIcon, ChevronLeftIcon, ChevronRightIcon, XIcon, PencilIcon } from 'lucide-react';
import { genreColors, formatIcons } from './constants';
import { chunkBooksIntoShelves } from './utils';
import { BookHoverCard } from './BookHoverCard';
import { useRoleTheme } from '../../../hooks/useRoleTheme';
interface BookDisplayProps {
  books: any[];
  viewMode: 'cover' | 'spine';
  darkMode: boolean;
  booksPerShelf?: number;
  hoveredBookId?: string | null;
  setHoveredBookId?: (id: string | null) => void;
  mousePosition?: {
    x: number;
    y: number;
  };
  title?: string;
  navigateToBookEdit?: (id: string) => void;
}
// Derive content tags from book genre
const getContentTags = (book: any): string[] => {
  const tags: string[] = [];
  if (book.genre?.includes('Fantasy')) {
    tags.push('Magic', 'Adventure');
  } else if (book.genre?.includes('Romance')) {
    tags.push('Slow Burn');
  } else if (book.genre?.includes('Mystery') || book.genre?.includes('Thriller')) {
    tags.push('Suspense', 'Plot Twists');
  } else if (book.genre?.includes('Horror')) {
    tags.push('Dark', 'Atmospheric');
  }
  if (book.type === 'anthology') tags.push('Short Stories');
  return tags.slice(0, 3);
};
// Unified responsive book info popup — bottom sheet on mobile, centered modal on desktop
export const VendorBookInfoModal: React.FC<{
  book: any;
  darkMode: boolean;
  onClose: () => void;
  onEdit: (bookId: string) => void;
}> = ({
  book,
  darkMode,
  onClose,
  onEdit
}) => {
  const rt = useRoleTheme();
  const bg = darkMode ? 'bg-gray-900' : 'bg-white';
  const border = darkMode ? 'border-gray-700' : 'border-gray-200';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-500';
  const contentTags = getContentTags(book);
  return <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center" onClick={onClose} data-id="element-17959" data-mp="2lzrjeun1xlzb">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-17960" data-mp="2cdqchiwofxp9" />
      <div className={`relative w-full md:max-w-md md:rounded-2xl rounded-t-2xl shadow-2xl border-t md:border ${bg} ${border}`} onClick={e => e.stopPropagation()} data-id="element-17961" data-mp="2ge0eyll9g5zd">
        {/* Handle (mobile only) */}
        <div className="flex justify-center pt-3 pb-1 md:hidden" data-id="element-17962" data-mp="27en53rt85fe4">
          <div className={`w-10 h-1 rounded-full ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`} data-id="element-17963" data-mp="2v3g45x5583cy" />
        </div>
        <button onClick={onClose} className={`absolute top-3 right-3 p-2 rounded-full ${darkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'} transition-colors`} data-id="element-17964" data-mp="303asbsxjaeub">
          <XIcon className="h-4 w-4" data-id="element-17965" data-mp="3e0pnleagxgu7" />
        </button>

        <div className="px-5 pb-6 pt-3 md:pt-5" data-id="element-17966" data-mp="3dywja5nbv8pd">
          <div className="flex gap-4 items-start" data-id="element-17967" data-mp="3isr8tppm6hwd">
            {/* Cover */}
            <div className="flex-shrink-0 w-24 h-36 rounded-xl overflow-hidden shadow-lg" data-id="element-17968" data-mp="17cgvxrq4ngxl">
              <img src={book.coverImage} alt={book.title} className="w-full h-full object-cover" data-id="element-17969" data-mp="12dvwhuip91a7" />
            </div>

            <div className="flex-1 min-w-0" data-id="element-17970" data-mp="1g6vcdxme8xia">
              <h3 className={`font-bold text-base leading-tight line-clamp-2 mb-0.5 ${textPrimary}`} data-id="element-17971" data-mp="1c375zi9i07ym">
                {book.title}
              </h3>
              {book.series && <p className="text-xs text-purple-500 mb-1 font-medium" data-id="element-17972" data-mp="jg343m5pc9r2">
                  {book.series}
                  {book.seriesNumber ? ` #${book.seriesNumber}` : ''}
                </p>}
              {book.coAuthor && <p className={`text-xs mb-1.5 ${textSecondary}`} data-id="element-17973" data-mp="28tpuinvqnksl">
                  with {book.coAuthor}
                </p>}

              {/* Tags — genre + special edition only (no type tag) */}
              <div className="flex flex-wrap gap-1 mb-1.5" data-id="element-17974" data-mp="2z51mf5x5uq0v">
                {book.genre && <span className={`text-xs px-2 py-0.5 ${rt.badgeRadius} font-medium ${darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'}`} data-id="element-17975" data-mp="343mmn5cex9pb">
                    {book.genre}
                  </span>}
                {book.edition === 'special' && <span className={`text-xs px-2 py-0.5 ${rt.badgeRadius} font-medium flex items-center gap-0.5 ${darkMode ? 'bg-amber-900/60 text-amber-200' : 'bg-amber-100 text-amber-700'}`} data-id="element-17976" data-mp="3i2mboe3u7qm0">
                    <SparklesIcon className="h-3 w-3" data-id="element-17977" data-mp="3ru8titaoknxq" />
                    Special Edition
                  </span>}
              </div>

              {/* Rating + year */}
              <div className="flex items-center gap-2" data-id="element-17978" data-mp="5ntvat2mt8kd">
                <div className="flex items-center gap-1" data-id="element-17979" data-mp="9nccpev8v1ig">
                  <StarIcon className="h-3.5 w-3.5 text-amber-500 fill-current" data-id="element-17980" data-mp="9lrjbsko1om6" />
                  <span className={`text-sm font-semibold ${textPrimary}`} data-id="element-17981" data-mp="1u7g42r703ju9">
                    {book.rating}
                  </span>
                </div>
                {book.releaseDate && <span className={`text-xs ${textSecondary}`} data-id="element-17982" data-mp="1l5qcnld4ckwh">
                    {book.releaseDate.substring(0, 4)}
                  </span>}
              </div>
            </div>
          </div>

          {/* Format pill */}
          {book.format && <div className="mt-3" data-id="element-17983" data-mp="28uj4p6qme8lt">
              <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full ${darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'}`} data-id="element-17984" data-mp="28vknxyhsi1em">
                {formatIcons[book.format]}
                <span data-id="element-17985" data-mp="2wkvk12o737mf">
                  {book.format.charAt(0).toUpperCase() + book.format.slice(1)}
                </span>
              </span>
            </div>}

          {/* Content tags — between format and actions */}
          {contentTags.length > 0 && <div className="mt-3 flex flex-wrap gap-1.5" data-id="element-17986" data-mp="txouc4p5nxvh">
              {contentTags.map(tag => <span key={tag} className={`text-xs px-2.5 py-1 rounded-full ${darkMode ? 'bg-purple-900/40 text-purple-200' : 'bg-purple-50 text-purple-700'}`} data-id="element-17987" data-mp="g6basqr8z00g">
                  {tag}
                </span>)}
            </div>}

          {/* Actions */}
          <div className="mt-4 flex gap-2" data-id="element-17988" data-mp="b7gsrr13tfhe">
            <button onClick={() => {
            onEdit(book.id);
            onClose();
          }} className="flex-1 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-semibold text-sm flex items-center justify-center gap-2 transition-colors" data-id="element-17989" data-mp="b545qzghoexh">
              <PencilIcon className="h-4 w-4" data-id="element-17990" data-mp="1wkjmtree5qxu" />
              Edit Book
            </button>
            <button onClick={onClose} className={`px-4 py-2.5 rounded-xl text-sm font-medium border transition-colors ${darkMode ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-id="element-17991" data-mp="1wi76fioa7omw">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>;
};
// Reusable cover carousel for ordered groups (series, co-writes by author, special editions)
// Uses native scroll — same pattern as BookCarousel / SeriesCarousel on the home page
export const CoverCarousel: React.FC<{
  books: any[];
  darkMode: boolean;
  onBookClick: (book: any) => void;
  onHoverEnter: (id: string) => void;
  onHoverLeave: () => void;
}> = ({
  books,
  darkMode,
  onBookClick,
  onHoverEnter,
  onHoverLeave
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const checkScroll = () => {
    if (scrollRef.current) {
      const {
        scrollLeft,
        scrollWidth,
        clientWidth
      } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };
  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) el.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);
    return () => {
      if (el) el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [books]);
  const scroll = (dir: 'left' | 'right') => {
    if (scrollRef.current) {
      const amount = scrollRef.current.clientWidth * 0.75;
      scrollRef.current.scrollBy({
        left: dir === 'left' ? -amount : amount,
        behavior: 'smooth'
      });
      setTimeout(checkScroll, 350);
    }
  };
  return <div className="relative group" data-id="element-17992" data-mp="2q15wztuwed8f">
      {/* Scroll container — single horizontal row */}
      <div ref={scrollRef} className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide" style={{
      scrollbarWidth: 'none',
      msOverflowStyle: 'none'
    }} onScroll={checkScroll} data-id="element-17993" data-mp="2uzqxlue2ayxe">
        {books.map((book: any) => <div key={book.id} className="flex-shrink-0 w-28 sm:w-32 cursor-pointer group/book" onClick={() => onBookClick(book)} onMouseEnter={() => onHoverEnter(book.id)} onMouseLeave={onHoverLeave} data-id="element-17994" data-mp="2venvrlfkes7x">
            <div className="w-full aspect-[2/3] bg-center bg-cover rounded-lg shadow-md transition-transform duration-200 ease-in-out group-hover/book:scale-[1.04] group-hover/book:shadow-xl" style={{
          backgroundImage: `url('${book.coverImage}')`
        }} title={book.title} data-id="element-17995" data-mp="2hfecudzz5k09" />
          </div>)}
      </div>

      {/* Left arrow */}
      {canScrollLeft && <button onClick={() => scroll('left')} className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 p-2 rounded-full shadow-md transition-colors ${darkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'}`} aria-label="Scroll left" data-id="element-17996" data-mp="6oejs4uag2t3">
          <ChevronLeftIcon className="h-4 w-4" data-id="element-17997" data-mp="2r45yzk48y1t" />
        </button>}

      {/* Right arrow */}
      {canScrollRight && <button onClick={() => scroll('right')} className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 p-2 rounded-full shadow-md transition-colors ${darkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'}`} aria-label="Scroll right" data-id="element-17998" data-mp="3b65su94eulv8">
          <ChevronRightIcon className="h-4 w-4" data-id="element-17999" data-mp="3g4780iypu11f" />
        </button>}
    </div>;
};
// Shelf-section renderer for a titled group of books (standalone, anthology,
// special editions, etc). Renders either a spine shelf or a cover grid.
export const BookDisplay: React.FC<BookDisplayProps> = ({
  books,
  viewMode,
  darkMode,
  booksPerShelf,
  hoveredBookId,
  setHoveredBookId,
  mousePosition,
  title,
  navigateToBookEdit
}) => {
  const [selectedBook, setSelectedBook] = useState<any | null>(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const safeBooks = Array.isArray(books) ? books : [];
  const displayBooks = typeof booksPerShelf === 'number' ? safeBooks.slice(0, booksPerShelf) : safeBooks;
  const handleSpineTap = (book: any) => {
    if (isMobile) {
      setSelectedBook(book);
    } else if (navigateToBookEdit) {
      navigateToBookEdit(book.id);
    } else {
      setSelectedBook(book);
    }
  };
  const SpineBook = ({
    book
  }: {
    book: any;
  }) => <div onClick={() => handleSpineTap(book)} onMouseEnter={() => !isMobile && setHoveredBookId?.(book.id)} onMouseLeave={() => setHoveredBookId?.(null)} className="relative flex flex-col items-center justify-center text-white font-medium rounded transition-transform duration-200 ease-in-out cursor-pointer text-center hover:scale-105 select-none flex-shrink-0" style={{
    width: book.edition === 'special' ? 68 : 56,
    height: book.edition === 'special' ? 250 : 220,
    fontSize: book.edition === 'special' ? '1rem' : '0.85rem',
    backgroundColor: genreColors[!book.coverImage ? book.genre : ''] || genreColors.default,
    backgroundImage: book.coverImage ? `url(${book.coverImage})` : undefined,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    marginBottom: 4
  }} title={book.title} data-id="element-18000" data-mp="1a8butqjuihz">
      {!book.coverImage && <div className="flex flex-col items-center px-1 w-full h-full justify-center" style={{
      writingMode: 'vertical-rl'
    }} data-id="element-18001" data-mp="1ba19o80qzmvo">
          <div className="w-full truncate" data-id="element-18002" data-mp="35mh9apz1g2rk">{book.title}</div>
          {book.seriesNumber && <div className="mt-2 text-xs font-normal opacity-80" data-id="element-18003" data-mp="rdotry1mg5l0">
              #{book.seriesNumber}
            </div>}
        </div>}
      <div className="absolute bottom-1 left-1 inline-flex items-center justify-center p-1 rounded bg-black bg-opacity-70 text-white text-xs z-10" title={book.format} data-id="element-18004" data-mp="1149qffxqj01a">
        {formatIcons[book.format]}
      </div>
      {book.edition === 'special' && <div className="absolute top-1 right-1 inline-flex items-center justify-center p-1 rounded-full bg-purple-600 bg-opacity-90 text-white text-xs z-10" data-id="element-18005" data-mp="8hoyad9fqjwo">
          <SparklesIcon className="h-3 w-3" data-id="element-18006" data-mp="hf92nv2lgwbi" />
        </div>}
    </div>;
  return <>
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6 mb-8`} data-id="element-18007" data-mp="mfwsidf37745">
        {title && <h2 className={`text-xl font-semibold mb-6 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-18008" data-mp="1sxa8d1tfk9qf">
            {title}
          </h2>}
        {displayBooks.length === 0 ? <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-18009" data-mp="1wey3p6do1o3p">
            No books to display.
          </p> : viewMode === 'spine' ? <div className="relative" data-id="element-18010" data-mp="25bglbmgg5pkz">
            <div className="flex items-end gap-2 overflow-x-auto pb-6 scrollbar-thin" style={{
          minHeight: 260
        }} data-id="element-18011" data-mp="21bycvq5tb6z1">
              {displayBooks.map((book: any) => <SpineBook key={book.id} book={book} data-id="element-18012" data-mp="2accxenayci1h" />)}
              <div className={`absolute bottom-0 left-0 right-0 h-2 ${darkMode ? 'bg-purple-900' : 'bg-purple-600'} rounded-full`} data-id="element-18013" data-mp="2p0qzux4gi4hi" />
            </div>
            {!isMobile && hoveredBookId && (() => {
          const hoveredBook = displayBooks.find((book: any) => book.id === hoveredBookId);
          return hoveredBook && mousePosition ? <BookHoverCard book={hoveredBook} darkMode={darkMode} mousePosition={mousePosition} data-id="element-18014" data-mp="3cncu9iboycre" /> : null;
        })()}
          </div> : <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6" data-id="element-18015" data-mp="37p1fct6jhc2b">
            {displayBooks.map((book: any) => <div key={book.id} className="relative flex flex-col items-center cursor-pointer group" onClick={() => setSelectedBook(book)} onMouseEnter={() => !isMobile && setHoveredBookId?.(book.id)} onMouseLeave={() => setHoveredBookId?.(null)} data-id="element-18016" data-mp="2ypeh2g93wmdh">
                <div className="relative w-full" data-id="element-18017" data-mp="2b14yrob3qgr3">
                  <div className="w-full aspect-[2/3] bg-center bg-cover rounded-lg shadow-md transition-transform duration-200 ease-in-out group-hover:scale-[1.03] group-hover:shadow-xl" style={{
              backgroundImage: `url('${book.coverImage}')`
            }} title={book.title} data-id="element-18018" data-mp="2fyx17c9dhl9j" />
                </div>
              </div>)}
          </div>}
      </div>
      {selectedBook && <VendorBookInfoModal book={selectedBook} darkMode={darkMode} onClose={() => setSelectedBook(null)} onEdit={navigateToBookEdit ?? (() => {})} data-id="element-18019" data-mp="14hkb56ws2ibn" />}
    </>;
};
export const SectionHeader = ({
  title,
  darkMode
}: {
  title: any;
  darkMode: any;
}) => <div className="flex justify-between items-center mb-4" data-id="element-18020" data-mp="qi1f3ch8qxfp">
    <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-18021" data-mp="qhhvlrby00zk">
      {title}
    </h2>
  </div>;